import { type ForwardedRef, useCallback } from 'react';
import { useField, useFormInstance } from '@adv-frontend/form-kit';
import { trackGoal } from '@/entities/trackGoal';
import { Dialog, DialogContent, DialogTitle } from '@/shared/components/Dialog';
import { withFormContext } from '@/shared/lib/form';
import { useCourierAddressLocation } from '../../lib/hooks/useCourierAddressLocation';
import type { CourierAddressPickerValue } from '../../lib/types/CourierAddressPickerValue';
import { COURIER_ADDRESS_FORM_ID, courierAddressForm } from '../../model/courierAddressForm';
import { CourierAddressForm } from '../CourierAddressForm/CourierAddressForm';
import { CourierAddressMap } from '../CourierAddressMap';
import styles from './CourierNewAddressDialog.module.css';

export interface CourierNewAddressDialogContentProps {
    isOpen: boolean;
    onClose: VoidFunction;
    onChange?: (value: CourierAddressPickerValue) => void;
    ref: ForwardedRef<unknown>;
}

export const CourierNewAddressDialog = withFormContext(
    courierAddressForm,
    COURIER_ADDRESS_FORM_ID
)<CourierNewAddressDialogContentProps>((props) => {
    const { formId, isOpen, onChange: onChangeProp, onClose: propOnClose } = props;
    const formInstance = useFormInstance({ id: formId, form: courierAddressForm });
    const { fields } = formInstance;
    const fullAddressField = useField(fields.fullAddress);

    const { location, handleChangeLocation } = useCourierAddressLocation(formId);

    const onClose = useCallback(() => {
        propOnClose();
        formInstance.reset();
    }, [formInstance, propOnClose]);

    const onChange = useCallback(
        async (values: CourierAddressPickerValue) => {
            onChangeProp?.(values);
            propOnClose();
            trackGoal('address_changed', { address_type: 'new' });
            formInstance.reinitialize({
                ...values,
                fullAddress: fullAddressField.value,
            });

            handleChangeLocation({ geocode: values.address, isDrag: false });
        },
        [onChangeProp, propOnClose, formInstance, fullAddressField.value, handleChangeLocation]
    );

    return (
        <Dialog
            isFullscreen
            isOpen={isOpen}
            onClose={onClose}
            className={styles.modal}
            containerClassName={styles.modalContainer}
        >
            <DialogTitle title="Укажите адрес доставки" />
            <DialogContent className={styles.content}>
                <CourierAddressForm formId={formId} className={styles.sidebar} onSubmit={onChange} />

                <div className={styles.mapWrapper}>
                    <div className={styles.map}>
                        <CourierAddressMap location={location} onChangeLocation={handleChangeLocation} />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
});
